/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef } from "react";
import {
  Badge,
  Button,
  CardImg,
  FormGroup,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Progress,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  CardTitle,
  CardText,
  CardSubtitle,
  UncontrolledTooltip
} from "reactstrap";
// CSS
import "./ContactUs.css";
// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUserGear } from "@fortawesome/free-solid-svg-icons";
import { faDiscord, faInstagram } from "@fortawesome/free-brands-svg-icons";
import { useTranslation, Trans } from "react-i18next";
import useReveal from "hooks/useReveal";




const ContactUs = () => {
  const mainRef = useRef(null);
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useReveal();

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, []);

  /* The navbar floats transparent over the navy hero and turns solid once the
     page scrolls, as on /contact — under this page's OWN body classes. Both
     must come off on unmount or every other route inherits a transparent,
     fixed navbar. */
  useEffect(() => {
    document.body.classList.add("contactus-nav-active");
    const onScroll = () => {
      document.body.classList.toggle("contactus-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("contactus-nav-active", "contactus-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);


  return (
    <>
      <DemoNavbar />
      <main ref={mainRef} className="contactus-page">
        <section className="contactus-hero contactus-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="contactus-orb contactus-orb-1" aria-hidden="true" />
          <span className="contactus-orb contactus-orb-2" aria-hidden="true" />

          <Container>
            <div className="contactus-support" data-reveal>
              <span className="contactus-support-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faUserGear} />
              </span>
              <h1 className="contactus-support-title">
                {t("contactUs.support")}
              </h1>
              <p className="contactus-support-text">
                {t("contactUs.forGeneralQuestions")}
              </p>
              <a className="contactus-support-email" href="mailto:codenec4everyone@gmail.com">
                <FontAwesomeIcon icon={faEnvelope} />
                <span>codenec4everyone@gmail.com</span>
              </a>
            </div>
          </Container>
        </section>

        <section className="contactus-community">
          <Container>
            <div className="contactus-head" data-reveal>
              <h2 className="contactus-title">{t("contactUs.connectWithCodenecCommunity")}</h2>
              <p className="contactus-lead">
                {t("contactUs.bePartOfLearningCommunity")}
              </p>
            </div>

            {/* Icon-only links, so each carries its name in aria-label. There
                is no Facebook link: the old one opened Instagram. */}
            <div
              className="contactus-socials"
              data-reveal
              style={{ "--reveal-delay": "80ms" }}
            >
              <Button
                className="contactus-social"
                color="white"
                href="https://www.instagram.com/codenec101?igsh=MXI3cWhvdDA2a2hseA=="
                id="tooltip837440414"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("contactUs.instagramLink")}
              >
                <span className="contactus-social-icon">
                  <FontAwesomeIcon icon={faInstagram} />
                </span>
              </Button>

              <Button
                className="contactus-social"
                color="white"
                href="https://discord.gg/zfjF8R8e9H"
                id="tooltip8298102022222"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("contactUs.discordLink")}
              >
                <span className="contactus-social-icon">
                  <FontAwesomeIcon icon={faDiscord} />
                </span>
                <UncontrolledTooltip delay={0} target="tooltip8298102022222">
                  {t("contactUs.discordTooltip")}
                </UncontrolledTooltip>
              </Button>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};


export default ContactUs;
